import React, { useState, useRef, useEffect, useContext } from "react";
import { cn, getStashed } from "../../lib/helpers";
import { SiteContext } from "../global/site-context";
import { validateDomain } from "./snowflake/play-finder";
import {
  Message,
  Org,
  HiddenFields,
  isDomainFree,
  WORKFLOW_ID,
  EMAIL_STATES,
  mapTeamSizeToCompanySize,
  TRACKABLE_FIELDS,
} from "./demo-ux";
import { countChatbotMessages, getChatbotGoal, countChatbotSessions, getChatbotTranscript, getChatbotPath, getSimpleDate } from "../../lib/client-attribution";
import { ATTRIBUTION_PARAM } from "./snowflake/navigation";
import { Button } from "../button";

const ORG_STATES = {
  NULL: "NULL",
  KNOWN: "KNOWN",
  UNKNOWN: "UNKNOWN"
};

const DEFAULT_ORG_SIZE = "1 - 75";

export const QuickDemoUX = () => {
  const site = useContext(SiteContext);
  const storage = site.metrics.analytics.storage;

  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState(EMAIL_STATES.NULL);
  const [message, setMessage] = useState("");
  const [lookupOrg, setLookupOrg] = useState({});
  const [emailLocked, setEmailLocked] = useState(false);
  const [orgState, setOrgState] = useState(ORG_STATES.NULL);
  const [calendarReady, setCalendarReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadId, setLeadId] = useState("");

  const emailRef = useRef(null);

  // Extract lead_id from URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const leadIdParam = urlParams.get('lead_id');
    if (leadIdParam) {
      setLeadId(leadIdParam);
    }
  }, []);

  // Load ChiliPiper script on mount
  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'chilipiper-concierge';
    script.src = 'https://commonroom.chilipiper.com/concierge-js/cjs/concierge.js';
    script.crossOrigin = 'anonymous';
    script.async = true;

    script.onerror = () => {
      console.error("Failed to load ChiliPiper script");
    };

    document.head.appendChild(script);

    return () => {
      const scriptEl = document.getElementById('chilipiper-concierge');
      if (scriptEl) scriptEl.remove();
    };
  }, []);

  const deployCalendar = (orgData = {}) => {
    const org = orgData || lookupOrg;

    if (window.ChiliPiper) {
      try {
        window.ChiliPiper.submit("commonroom", "linkedin-convo-ads--no-ai-dq---router", {
          domElement: '#chilipiper-calendar-container',
          formId: 'quickDemoUX',
          map: true,
          lead: {
            ...org,
            email,
            "employee_count___range": mapTeamSizeToCompanySize(org?.Team) || DEFAULT_ORG_SIZE,
            "company": org?.Name || null,
            "chatbot_engaged": String(countChatbotMessages()),
            "chatbot_sessions": String(countChatbotSessions()),
            "chatbot_goal": getChatbotGoal() || "",
            "chatbot_transcript": getChatbotTranscript(),
            "chatbot_path": getChatbotPath() || "",
          },
          onSuccess: () => {
            // Booked a demo
          },
          onError: (err) => {
            // Not a booking match
          },
          onClose: (e) => {
            // Exited the calendar
          },
          onRouted: (e) => {
            // Data received, routing attempt next
          },
          onDisqualified: () => {
            // Not a booking match
          },
        });
        setCalendarReady(true);
      } catch (error) {
        console.error("Failed to deploy ChiliPiper calendar:", error);
      }
    } else {
      // Script not loaded yet, try again after delay
      setTimeout(deployCalendar, 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent re-submission
    if (!email || emailLocked || isSubmitting) return;

    setIsSubmitting(true);

    const domain = email.split('@')[1]?.toLowerCase();

    // Validate domain format
    if (!validateDomain(domain)) {
      console.debug("Invalid domain:", domain);
      setEmailValid(EMAIL_STATES.INVALID);
      setMessage("Invalid email.");
      setIsSubmitting(false);
      return;
    }

    // Check for free email domains
    if (isDomainFree(domain)) {
      console.debug("Free email service detected:", domain);
      setEmailValid(EMAIL_STATES.INVALID);
      setMessage("Use a business email from your company domain.");
      setIsSubmitting(false);
      return;
    }

    console.debug("Valid email domain:", domain);
    setEmailValid(EMAIL_STATES.VALID);
    setMessage("");

    // Log event for metrics
    site.metrics.logEvent({
      label: "send",
      category: "button_click",
      action: "quick_demo_ux",
    });

    // API Lookup (await completion before deploying calendar)
    let lookupData = null;
    try {
      const response = await fetch("/.netlify/functions/airops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          inputs: {
            email: email,
            domain: domain,
            workflowId: WORKFLOW_ID,
            token: site.buildToken
          }
        })
      });

      const data = await response.json();
      console.debug("Lookup response:", data);

      if (response.ok && data.result) {
        console.debug("Company found.");
        lookupData = data.result;
        setLookupOrg(data.result);
        setOrgState(ORG_STATES.KNOWN);
        setEmailLocked(true);
        storage.setItem("user.email", email);
      } else {
        console.debug("Company not found.");
        setOrgState(ORG_STATES.UNKNOWN);
      }
    } catch (error) {
      console.error("Error calling lookup endpoint:", error);
      setOrgState(ORG_STATES.UNKNOWN);
    }

    // Deploy calendar AFTER lookup completes (pass fresh data directly)
    if (!calendarReady) {
      deployCalendar(lookupData);
    }

    // Identify with HubSpot tracking script
    if (window._hsq) {
      window._hsq.push(["identify", { email }]);
    }

    // Server-side HubSpot fallback
    const hutk = document.cookie.split(";").find((c) => c.trim().startsWith("hubspotutk="))?.split("=")[1] || "";
    const params = new URLSearchParams(window.location.search);
    const utmFields = TRACKABLE_FIELDS.reduce((acc, id) => {
      acc[`utm_parameter___${id}`] = params.get(`utm_${id}`) || "";
      acc[`utm_parameter___${id}_original`] = storage.getItem(`origin.urlParams.${id}`) || "";
      return acc;
    }, {});
    fetch("/.netlify/functions/hubspot-submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        formId: "quickDemoUX",
        hutk,
        pageUri: window.location.href,
        pageName: document.title,
        fields: {
          email,
          company_name: lookupData?.Name ?? "",
          domain: lookupData?.Domain ?? "",
          employee_count___range: mapTeamSizeToCompanySize(lookupData?.Team) || DEFAULT_ORG_SIZE,
          company_offering: lookupData?.["Key Offering"] ?? "",
          company_industry: lookupData?.Industry ?? "",
          company_team: lookupData?.Team ?? "",
          company_ai_score: lookupData?.AI ?? "",
          company_open_source: lookupData?.["Open Source"] ?? "",
          company_social: lookupData?.Social ?? "",
          company_funding: lookupData?.Funding ?? "",
          company_web_traffic: lookupData?.["Web Traffic"] ?? "",
          chatbot_engaged: String(countChatbotMessages()),
          chatbot_sessions: String(countChatbotSessions()),
          chatbot_goal: getChatbotGoal() || "",
          chatbot_transcript: getChatbotTranscript() || "",
          chatbot_path: getChatbotPath() || "",
          ...(leadId && { lead_id: leadId }),
          page_cta_path_on_website: params.get(ATTRIBUTION_PARAM) || "",
          utm_parameter___created_date: getSimpleDate(
            `urlParams.${TRACKABLE_FIELDS.find((f) => getStashed({ id: `urlParams.${f}`, storage }))}.created`,
            storage
          ) || "",
          utm_parameter___created_date_original: getSimpleDate(
            `origin.urlParams.${TRACKABLE_FIELDS.find((f) => storage.getItem(`origin.urlParams.${f}`))}.created`,
            storage
          ) || "",
          ...utmFields,
        },
      }),
    }).catch((err) => console.error("HubSpot fallback submit failed:", err));

    setIsSubmitting(false);
  };

  return (
    <div className="relative mt-12 md:mt-16 lg:mt-20">
      <style>{`
        #chilipiper-calendar-container {
          background-color: rgb(250, 250, 251);
        }
        #chilipiper-calendar-container iframe {
          min-height: 600px !important;
          height: 100% !important;
          border-radius: 0.75rem;
        }
      `}</style>
      <div className="p-3 md:p-6 pb-10 mb-8 rounded-2xl shadow-lg bg-light-30"
        style={{ backgroundColor: "rgb(250, 250, 251)" }}
      >
        <form id="quickDemoUX" onSubmit={handleSubmit} data-hs-do-not-collect="true">
          {/* Email Field with Submit Button */}
          <div className="mb-4 flex gap-2">
            <input
              ref={emailRef}
              autoFocus
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={emailLocked}
              className={cn(
                "flex-1 p-3 rounded-lg transition-all duration-200",
                "bg-white border border-dark-10 focus:outline-none focus:ring-0",
                "outline-none focus:ring-0",
                emailLocked
                  ? "border-green-500 opacity-75 cursor-not-allowed"
                  : emailValid === EMAIL_STATES.INVALID
                    ? "border-orange-500"
                    : "border-transparent focus:border-green-emerald"
              )}
              placeholder="Your work email"
              required={true}
              style={{ colorScheme: "dark" }}
              autoComplete="email"
            />
            <Button
              text={isSubmitting ? "Processing..." : "Book time"}
              deactivated={emailLocked || isSubmitting}
              className="whitespace-nowrap px-8"
              metrics={{
                action: "email-calendar",
                label: "book time",
              }}
              isBig={true}
            />
          </div>

          {/* Hidden input to ensure email is submitted if field is disabled */}
          {emailLocked && (
            <input type="hidden" name="email_value" value={email} />
          )}

          {/* Hidden input for lead_id from URL parameter */}
          {leadId && (
            <input type="hidden" name="lead_id" value={leadId} />
          )}

          {/* Loading Indicator */}
          {isSubmitting && (
            <div className="flex items-center gap-2 text-light-50 text-sm mt-4">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-emerald"></div>
              <span>Looking up your company...</span>
            </div>
          )}

          {message && (
            <Message message={message} className="mt-6 mb-2" />
          )}

          {/* Company Info Badges */}
          <Org org={lookupOrg} orgState={orgState} />

          {/* ChiliPiper Calendar Container */}
          <div
            id="chilipiper-calendar-container"
            className="w-full rounded-xl overflow-hidden my-8 px-3"
            style={{ minHeight: '600px' }}
          >
            {/* Placeholder - shown until calendar is deployed */}
            {!calendarReady && (
              <div className="flex items-center justify-center">
                <img
                  src="/static/img/calendar-mock.png"
                  alt="Calendar preview"
                  style={{ filter: "blur(4px)" }}
                />
              </div>
            )}
          </div>

          {/* Hidden Attribution Fields */}
          <HiddenFields storage={storage} org={lookupOrg} />
        </form>

        {/* Footer Links */}
        <p className="text-center text-sm text-light-20 hover:text-light-50 transition-colors duration-300 mt-4">
          Agree to{" "}
          <a
            href="/terms-of-use/"
            className="hover:underline"
            target="_blank"
          >
            terms
          </a>{" "}
          and{" "}
          <a
            href="/privacy-policy/"
            className="hover:underline"
            target="_blank"
          >
            privacy policy
          </a>.
        </p>
        <p className="text-center mt-2">
          <a
            href="mailto:marketing@commonroom.io"
            target="_blank"
            className="text-sm text-light-20 hover:text-link transition-colors duration-300"
          >
            Having trouble?
          </a>
        </p>
      </div>
    </div>
  );
};
